import { Component, computed, inject, Inject, input, Optional } from '@angular/core';
import { Params } from '@angular/router';

import { toSignal } from '@angular/core/rxjs-interop';
import { select } from '@ngxs/store';

import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { params } from '@app/configurations';
import { ApiListMutator } from '@app/core/api/api-list-mutator.class';
import { CustomerApiService } from '@app/core/api/customer-api.service';
import { Customer } from '@app/core/models/customer.models';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { RouterNavState } from '@app/core/store/states/router-nav/router-nav.state';
import { filterObject } from '@app/core/utilities/object.utilities';
import { TranslatePipe } from '@ngx-translate/core';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { IconComponent } from '../icon/icon.component';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';

@Component({
  selector: 'adspt-customer-selector',
  templateUrl: './customer-selector.component.html',
  styleUrls: ['./customer-selector.component.scss'],
  imports: [
    NgClass,
    NgTemplateOutlet,
    TranslatePipe,
    NzInputModule,
    NzDropDownModule,
    IconComponent,
    AsyncPipe,
    NzSpinComponent,
    NzTooltipDirective,
  ],
})
export class CustomerSelectorComponent {
  readonly forSignup = input(false);
  readonly customer = select(CustomerState.customer);
  readonly cs = inject(CustomerApiService);
  readonly params = select(RouterNavState.queryParams);

  readonly customersMutator = new ApiListMutator<Customer>(
    (req) =>
      this.cs.getList({
        ...req,
        route: [],
        filterValues: this.forSignup() ? { archived: false, canceled: false } : { archived: false },
      }),
    { searchFields: ['customerId', 'name'], limit: 15 },
    { trackById: 'customerId', searchDebounceTime: 500 },
  );

  readonly customers = toSignal(this.customersMutator.list$);
  readonly formatedCustomers = computed(() =>
    this.customers().map((customer) => ({ ...customer, url: this.getURL(customer, this.params()) })),
  );
  readonly loadState = computed(() => (Array.isArray(this.customers()) ? (this.customers().length ? 1 : 0) : -1));

  constructor(@Optional() @Inject(NZ_MODAL_DATA) data: Partial<CustomerSelectorComponent>) {
    Object.keys(data || {}).forEach((key) => (this[key] = data[key]));
  }

  onScroll(event: Event): void {
    const container = event.target as HTMLElement;

    if (container.offsetHeight + container.scrollTop >= container.scrollHeight) {
      this.customersMutator.loadMore();
    }
  }

  getURL(item: Customer, queryParams: Params) {
    const tmpParams = filterObject({
      [params.CUSTOMER_ID]: item.customerId.toString(),
      [params.GLOBAL_FILTER_DATE_RANGE]: queryParams[params.GLOBAL_FILTER_DATE_RANGE],
      [params.IMPERSONATION_ID]: queryParams[params.IMPERSONATION_ID],
    });

    const path = location.pathname.toLowerCase().indexOf('no-access') !== -1 ? '/monitor' : location.pathname;

    return `${location.origin}${path}?${new URLSearchParams(tmpParams).toString()}`;
  }
}
